import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Button,
    Grid,
    Breadcrumbs,
    Container,
    Tooltip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ExpandMore, ChevronRight, Download, InfoOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportAPI } from '../../services/reportAPI';
import { formatDateForInput } from '../../utils/dateUtils';
import { AccountingReconciliation, ReconciliationData, reconciliationSections } from './AccountingReconciliation';

interface RevenueBreakdownItem {
    category: string;
    subcategory?: string;
    amount: number;
    tax: number;
    totalAmount: number;
    refunds: number;
    outstanding: number;
    /** Explicit columns from the backend (credit = collected, debit = refunded, net = credit − debit) */
    credit?: number;
    debit?: number;
    net?: number;
    /** HST reversed by refunds in the period */
    refundTax?: number;
    isSubcategory?: boolean;
    level?: number;
    isLiability?: boolean;
    /** Level-2 amenity rows: the amenity type (level-1 row) they belong to */
    groupSubcategory?: string;
}

/** Column values for a row — explicit from the backend, with the older derivation as a fallback */
const rowColumns = (item: RevenueBreakdownItem) => ({
    credit: item.credit ?? (item.isLiability ? 0 : item.amount + Math.abs(item.refunds) + item.tax),
    debit: item.debit ?? item.refunds,
    taxReversed: item.refundTax ?? 0,
    net: item.net ?? (item.isLiability ? item.amount : item.amount + item.tax)
});

/** The level-1 row a level-2 row sits under */
const isChildOf = (child: RevenueBreakdownItem, parent: RevenueBreakdownItem) =>
    child.category === parent.category &&
    (child.groupSubcategory !== undefined
        ? child.groupSubcategory === parent.subcategory
        : !!child.subcategory?.includes(parent.subcategory || ''));

export const AccountingReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [revenueBreakdown, setRevenueBreakdown] = useState<RevenueBreakdownItem[]>([]);
    
    // Initialize date range to current month
    const now = new Date();
    const [startDate, setStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const [arCollected, setArCollected] = useState<number>(0);
    const [reportWarnings, setReportWarnings] = useState<string[]>([]);
    const [cancelledTransactions, setCancelledTransactions] = useState<number>(0);
    const [writtenOffTransactions, setWrittenOffTransactions] = useState<number>(0);
    const [writtenOffTax, setWrittenOffTax] = useState<number>(0);
    const [reconciliation, setReconciliation] = useState<ReconciliationData>({});
    const [approximateDateCount, setApproximateDateCount] = useState<number>(0);

    // Track expanded categories for collapsible subcategories
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['Membership Payments']));

    useEffect(() => {
        loadReportData();
    }, [startDate, endDate]);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const startDateStr = formatDateForInput(startDate);
            const endDateStr = formatDateForInput(endDate);

            console.log('[AccountingReport] Fetching data for:', startDateStr, 'to', endDateStr);

            // Fetch transaction stats for selected period
            const stats = await reportAPI.getTransactionStats({
                startDate: startDateStr,
                endDate: endDateStr
            });

            console.log('[AccountingReport] Revenue breakdown:', stats.revenueBreakdown);

            setRevenueBreakdown(stats.revenueBreakdown || []);
            setArCollected(stats.arCollected || 0);
            setReportWarnings(stats.warnings || []);
            setCancelledTransactions(stats.cancelledTransactions || 0);
            setWrittenOffTransactions(stats.writtenOffTransactions || 0);
            setWrittenOffTax(stats.writtenOffTax || 0);
            setReconciliation({
                arRollForward: stats.arRollForward,
                walletRollForward: stats.walletRollForward,
                giftCards: stats.giftCards,
                paymentsReceived: stats.paymentsReceived
            });
            setApproximateDateCount(stats.approximateDateCount || 0);
        } catch (err: any) {
            console.error('Error loading accounting report:', err);
            setError(err.message || 'Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    const handleExportCSV = () => {
        if (!revenueBreakdown || revenueBreakdown.length === 0) return;

        // Get visible rows (respecting expanded/collapsed state)
        const visibleRows = revenueBreakdown.filter(item => {
            if (!item.isSubcategory) return true;
            return expandedCategories.has(item.category);
        });

        // Create CSV content
        const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
        const headers = ['Category', 'Credit', 'Debit', 'Tax', 'Tax Reversed', 'Outstanding', 'Net'];
        const csvRows = visibleRows.map(item => {
            const c = rowColumns(item);
            return [
                csvCell(item.subcategory || item.category),
                c.credit.toFixed(2),
                c.debit.toFixed(2),
                item.tax.toFixed(2),
                c.taxReversed.toFixed(2),
                item.outstanding.toFixed(2),
                c.net.toFixed(2)
            ];
        });

        // Add AR Collected row if present
        if (arCollected > 0) {
            csvRows.push([
                'Accounts Receivable Collected',
                '0.00',
                '0.00',
                '0.00',
                '0.00',
                '0.00',
                arCollected.toFixed(2)
            ]);
        }

        // Add Cancelled Transactions row if present
        if (cancelledTransactions > 0) {
            csvRows.push([
                'Cancelled Transactions',
                '0.00',
                '0.00',
                '0.00',
                '0.00',
                '0.00',
                `-${cancelledTransactions.toFixed(2)}`
            ]);
        }

        // Add Written-Off Transactions row if present
        if (writtenOffTransactions > 0) {
            csvRows.push([
                'Written-Off Transactions',
                '0.00',
                '0.00',
                writtenOffTax.toFixed(2),
                '0.00',
                '0.00',
                `-${writtenOffTransactions.toFixed(2)}`
            ]);
        }

        // Reconciliation sections below the table
        const reconciliationLines: string[] = [];
        for (const section of reconciliationSections(reconciliation)) {
            reconciliationLines.push('', csvCell(section.title));
            for (const line of section.lines) {
                reconciliationLines.push([csvCell(line.label), csvCell(line.note ?? ''), line.amount.toFixed(2)].join(','));
            }
        }

        const csvContent = [
            headers.join(','),
            ...csvRows.map(row => row.join(',')),
            ...reconciliationLines
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `accounting-report-${formatDateForInput(startDate)}-to-${formatDateForInput(endDate)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Accounting Report</Typography>
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Accounting Report
                </Typography>

                {reportWarnings.map((w, i) => (
                    <Alert key={i} severity="warning" sx={{ mb: 2 }}>{w}</Alert>
                ))}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Date Range Selector */}
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => newValue && setStartDate(newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small'
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => newValue && setEndDate(newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small'
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Revenue Summary Table */}
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            Revenue Summary
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Download />}
                            onClick={handleExportCSV}
                            disabled={!revenueBreakdown || revenueBreakdown.length === 0}
                        >
                            Export CSV
                        </Button>
                    </Box>
                    {revenueBreakdown && revenueBreakdown.length > 0 ? (
                        <DataGrid
                            rows={
                                (() => {
                                    const dataRows = revenueBreakdown
                                        .filter(item => {
                                            const level = item.level ?? (item.isSubcategory ? 1 : 0);
                                            
                                            // Always show level 0 (parent categories)
                                            if (level === 0) return true;
                                            
                                            // For level 1 (types/tiers), show if parent is expanded
                                            if (level === 1) {
                                                return expandedCategories.has(item.category);
                                            }
                                            
                                            // For level 2 (individual items), find parent level 1 and check if expanded
                                            if (level === 2) {
                                                const parentLevel1 = revenueBreakdown.find(
                                                    parent => parent.level === 1 && isChildOf(item, parent)
                                                );
                                                
                                                if (parentLevel1) {
                                                    const parentKey = `${parentLevel1.category}::${parentLevel1.subcategory}`;
                                                    return expandedCategories.has(parentKey);
                                                }
                                                return false;
                                            }
                                            
                                            return false;
                                        })
                                        .map((item, index) => {
                                            const level = item.level ?? (item.isSubcategory ? 1 : 0);

                                            // Check if this item has children
                                            let hasChildren = false;
                                            if (level === 0) {
                                                hasChildren = revenueBreakdown.some(child => child.level === 1 && child.category === item.category);
                                            } else if (level === 1) {
                                                hasChildren = revenueBreakdown.some(
                                                    child => child.level === 2 && isChildOf(child, item)
                                                );
                                            }

                                            const c = rowColumns(item);

                                            return {
                                                id: index,
                                                category: item.subcategory || item.category,
                                                credit: c.credit,
                                                debit: c.debit,
                                                tax: item.tax,
                                                taxReversed: c.taxReversed,
                                                outstanding: item.outstanding,
                                                net: c.net,
                                                level: level,
                                                parentCategory: item.category,
                                                parentSubcategory: item.subcategory,
                                                hasChildren: hasChildren,
                                                expandKey: level === 0 ? item.category : `${item.category}::${item.subcategory}`,
                                                isLiability: item.isLiability ?? false
                                            };
                                        });

                                    // Calculate totals from level 0 revenue rows only (exclude liabilities)
                                    const parentRows = revenueBreakdown.filter(item => {
                                        const level = item.level ?? (item.isSubcategory ? 1 : 0);
                                        return level === 0 && !item.isLiability;
                                    });
                                    
                                    const totalCredit = parentRows.reduce((sum, item) => sum + rowColumns(item).credit, 0);
                                    const totalDebit = parentRows.reduce((sum, item) => sum + rowColumns(item).debit, 0);
                                    const totalTax = parentRows.reduce((sum, item) => sum + item.tax, 0);
                                    const totalTaxReversed = parentRows.reduce((sum, item) => sum + rowColumns(item).taxReversed, 0);
                                    const totalOutstanding = parentRows.reduce((sum, item) => sum + item.outstanding, 0);
                                    const totalNet = parentRows.reduce((sum, item) => sum + rowColumns(item).net, 0) + arCollected;

                                    // Add AR Collected row (prior-period invoices collected in this period) before TOTAL
                                    if (arCollected > 0) {
                                        dataRows.push({
                                            id: -1,
                                            category: 'Accounts Receivable Collected',
                                            credit: 0,
                                            debit: 0,
                                            tax: 0,
                                            taxReversed: 0,
                                            outstanding: 0,
                                            net: arCollected,
                                            level: 0,
                                            parentCategory: '',
                                            parentSubcategory: '',
                                            hasChildren: false,
                                            expandKey: 'ar_collected',
                                            isLiability: false
                                        });
                                    }

                                    // Add Cancelled Transactions row (prior-period transactions cancelled in this period)
                                    if (cancelledTransactions > 0) {
                                        dataRows.push({
                                            id: -2,
                                            category: 'Cancelled Transactions',
                                            credit: 0,
                                            debit: 0,
                                            tax: 0,
                                            taxReversed: 0,
                                            outstanding: 0,
                                            net: -cancelledTransactions,
                                            level: 0,
                                            parentCategory: '',
                                            parentSubcategory: '',
                                            hasChildren: false,
                                            expandKey: 'cancelled_transactions',
                                            isLiability: false
                                        });
                                    }

                                    // Add Written-Off Transactions row
                                    if (writtenOffTransactions > 0) {
                                        dataRows.push({
                                            id: -3,
                                            category: 'Written-Off Transactions',
                                            credit: 0,
                                            debit: 0,
                                            tax: writtenOffTax,
                                            taxReversed: 0,
                                            outstanding: 0,
                                            net: -writtenOffTransactions,
                                            level: 0,
                                            parentCategory: '',
                                            parentSubcategory: '',
                                            hasChildren: false,
                                            expandKey: 'written_off_transactions',
                                            isLiability: false
                                        });
                                    }

                                    return [
                                        ...dataRows,
                                        {
                                            id: 'total',
                                            category: 'TOTAL',
                                            credit: totalCredit,
                                            debit: totalDebit,
                                            tax: totalTax,
                                            taxReversed: totalTaxReversed,
                                            outstanding: totalOutstanding,
                                            net: totalNet,
                                            level: -1,
                                            parentCategory: '',
                                            parentSubcategory: '',
                                            hasChildren: false,
                                            expandKey: 'total',
                                            isTotal: true
                                        }
                                    ];
                                })()
                            }
                            columns={[
                                {
                                    field: 'category',
                                    headerName: 'Category',
                                    flex: 1,
                                    minWidth: 200,
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                pl: params.row.isTotal ? 0 : params.row.level * 4,
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                width: '100%',
                                                borderTop: params.row.isTotal ? '2px solid' : params.row.isLiability && params.row.level === 0 ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : params.row.isLiability && params.row.level === 0 ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : params.row.isLiability && params.row.level === 0 ? 1 : 0,
                                                color: params.row.isLiability ? 'warning.dark' : 'inherit'
                                            }}
                                        >
                                            {params.row.hasChildren && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleCategory(params.row.expandKey)}
                                                    sx={{ mr: 1, p: 0 }}
                                                >
                                                    {expandedCategories.has(params.row.expandKey) ? (
                                                        <ExpandMore fontSize="small" />
                                                    ) : (
                                                        <ChevronRight fontSize="small" />
                                                    )}
                                                </IconButton>
                                            )}
                                            {params.value}
                                            {params.row.expandKey === 'ar_collected' && (
                                                <Tooltip
                                                    title="Prior transactions that were collected during this report period."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                            {params.row.expandKey === 'cancelled_transactions' && (
                                                <Tooltip
                                                    title="Charges billed (and still unpaid) in an earlier period that were cancelled during this period. Reduces accounts receivable. A charge cancelled in the same period it was billed is a void and doesn't appear anywhere."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                            {params.row.expandKey === 'written_off_transactions' && (
                                                <Tooltip
                                                    title="Charges written off during this period, whatever their date. The member owed these amounts but collection is not expected. A charge written off in the period it was billed also shows as a sale (Outstanding)."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                            {params.row.expandKey === 'Liabilities' && (
                                                <Tooltip
                                                    title="Amounts the club owes to members. Not included in the TOTAL."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                            {params.row.expandKey === 'Liabilities::Wallet Credits' && (
                                                <Tooltip
                                                    title="Change in member wallet balances this period, at the full (gross) amount: credits issued (refunds, admin credits, gift cards) minus wallet used to pay charges. See the wallet roll-forward below."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                            {params.row.expandKey === 'Liabilities::F&B Tips (payable to staff)' && (
                                                <Tooltip
                                                    title="Tips collected on F&B tabs, less any tips refunded with a refunded member-account charge. Owed to staff, not revenue."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                            {params.row.expandKey === 'Liabilities::Personal Training' && (
                                                <Tooltip
                                                    title="Remaining value of active personal training packs purchased in this period (remaining sessions × cost per session)."
                                                    arrow
                                                    placement="right"
                                                >
                                                    <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'credit',
                                    headerName: 'Credit',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="Collected in this period (card, wallet or marked paid) for charges dated in this period. Prior-period charges collected now are in Accounts Receivable Collected." arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Credit</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'debit',
                                    headerName: 'Debit',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="Refunds issued in this period (gross), under the original charge's category, whatever the charge's date. Food & Beverage and Pro Shop show refunds of the period's own sales instead (including member-account refunds, with their HST), whatever day the refund was issued. Where each refund went (card or wallet) is in Payments received below." arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Debit</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                color: params.value > 0 ? 'error.main' : 'inherit',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'tax',
                                    headerName: 'Tax',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'taxReversed',
                                    headerName: 'Tax Reversed',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="HST reversed by this period's refunds (part of the Debit). Refunds to a member's wallet reverse the HST too." arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Tax Reversed</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                color: params.value > 0 ? 'error.main' : 'inherit',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'outstanding',
                                    headerName: 'Outstanding',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="Charges dated in this period that hadn't been collected by the end of the period (as of that date — later payments or cancellations don't change a past period). Credit + Outstanding = sales. For Food & Beverage and Pro Shop these are member-account charges that are still pending (unresolved) and need review; they are sales but not in Net." arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Outstanding</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                color: params.value > 0 ? 'warning.main' : 'inherit',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                },
                                {
                                    field: 'net',
                                    headerName: 'Net',
                                    flex: 0.7,
                                    minWidth: 120,
                                    align: 'right',
                                    headerAlign: 'right',
                                    renderHeader: () => (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            <Tooltip title="Credit minus Debit (refunds), plus Accounts Receivable collected from prior periods. Matches the Payments received total below." arrow placement="top">
                                                <InfoOutlined sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help' }} />
                                            </Tooltip>
                                            <strong>Net</strong>
                                        </Box>
                                    ),
                                    valueFormatter: (params) => formatCurrency(params as number),
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                fontWeight: params.row.isTotal ? 'bold' : params.row.level === 0 ? 'bold' : params.row.level === 1 ? 600 : 'normal',
                                                borderTop: params.row.isTotal ? '2px solid' : 'none',
                                                borderColor: params.row.isTotal ? 'divider' : 'transparent',
                                                pt: params.row.isTotal ? 1 : 0
                                            }}
                                        >
                                            {formatCurrency(params.value)}
                                        </Box>
                                    )
                                }
                            ] as GridColDef[]}
                            autoHeight
                            hideFooter
                            disableRowSelectionOnClick
                            disableColumnMenu
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid rgba(224, 224, 224, 1)'
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'background.default',
                                    borderBottom: '2px solid rgba(224, 224, 224, 1)'
                                }
                            }}
                        />
                    ) : (
                        <Typography color="textSecondary" variant="body2" textAlign="center">
                            No revenue breakdown available
                        </Typography>
                    )}
                    {approximateDateCount > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {approximateDateCount} charge(s) in this period rely on an estimated cancellation or refund date
                            (recorded before status changes were tracked). The drill-down marks them.
                        </Alert>
                    )}
                    <AccountingReconciliation data={reconciliation} />
                </Paper>
            </Box>
        </LocalizationProvider>
        </Container>
    );
};
